// ===========================================================================

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  IconButton,
  Avatar,
  Divider,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  IconMapPin,
  IconCurrencyRupee,
  IconLeaf,
  IconTruck,
  IconPhone,
  IconBrandWhatsapp,
  IconChevronLeft,
  IconHeart,
  IconStar,
  IconEye,
  IconCheck,
  IconInfoCircle,
  IconUsers,
  IconArrowRight,
  IconTrash,
  IconClock,
  IconHistory,
  IconExternalLink,
  IconBolt,
} from "@tabler/icons-react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { colors, fonts, size } from "../../ColorFont/color";
import { url } from "../../ConstUrl/Url";
import axios from "axios";
import { toast } from "react-toastify";
import SponsoredHoursPopup from "../../pages/SponsoredHoursPopup";

// ─── Constants ──────────────────────────────────────────────────────────────────
// PRIMARY and PRIMARY_DARK are now used directly from colors object to ensure reactivity

const CropDetails = ({ userData }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // ─── State ──────────────────────────────────────────────────────────────────
  const [crop, setCrop] = useState(null);
  const [seller, setSeller] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showLogoutPrompt, setShowLogoutPrompt] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const [isRevealing, setIsRevealing] = useState(false);
  const [sponsorshipHistory, setSponsorshipHistory] = useState([]);
  const [isSponsorPopupOpen, setIsSponsorPopupOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isOwner = crop && userData && (
    userData?._id === crop?.user ||
    userData?._id === (crop?.user?._id || crop?.user)
  );

  // ─── Fetch Data ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        // 1. Fetch Crop (with cache-busting)
        const response = await axios.get(
          `${url}/api/get-single-plot/${id}?t=${new Date().getTime()}`,
        );
        const cropData = response.data;
        if (!cropData) throw new Error("Crop not found");
        setCrop(cropData);

        // 2. Fetch Seller Details directly by ID
        const sellerId = cropData.user?._id || cropData.user;
        if (sellerId) {
          try {
            const sellerRes = await axios.get(
              `${url}/api/users/get-user-by-id/${sellerId}`,
            );
            if (sellerRes.data) {
              setSeller(sellerRes.data);
            }
          } catch (userErr) {
            console.error("Error fetching seller info:", userErr);
          }
        }
      } catch (error) {
        console.error("Error fetching details:", error);
        toast.error(t("common:errorLoadingDetails") || "Failed to load crop details");
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id, location.key]);

  useEffect(() => {
    if (id && isOwner) {
      fetchSponsorshipHistory();
    }
  }, [id, isOwner]);

  const fetchSponsorshipHistory = async () => {
    try {
      const res = await axios.get(`${url}/api/plots/${id}/sponsorship-history`);
      setSponsorshipHistory(res.data || []);
    } catch (err) {
      console.error("Error fetching sponsorship history:", err);
    }
  };

  // Update isFavorite state when userData or crop changes
  useEffect(() => {
    if (userData?.wishlist && crop?._id) {
      setIsFavorite(userData.wishlist.includes(crop._id));
    }
  }, [userData, crop]);

  const hasPurchased = userData?.purchasedPlots?.some(
    (p) => (p.plotId?._id || p.plotId) === crop?._id
  );

  useEffect(() => {
    if (hasPurchased) {
      setIsRevealed(true);
    }
  }, [hasPurchased]);

  const handleGetContact = async () => {
    if (!userData) {
      toast.info(t("common:loginRequiredToContact") || "Please Login to see Contact Details");
      window.location.href = "/home?openLogin=true";
      return;
    }

    if (isRevealing) return;

    const userId = userData?._id;
    const plotId = crop?._id;

    // Check if enough contacts left
    if (userData.noOfContacts <= 0 && !hasPurchased) {
      navigate("/buyer/boost", { state: { cropData: crop } });
      return;
    }

    try {
      setIsRevealing(true);
      const response = await axios.post(`${url}/api/view-plot-details`, {
        userId,
        plotId,
      });

      if (response.status === 200) {
        const data = response.data;
        // Update local state
        setIsRevealed(true);
        toast.success("Contact Details Revealed!");

        // Sync globally
        if (data.user) {
          localStorage.setItem("userData", JSON.stringify(data.user));
          window.dispatchEvent(new Event("userDataUpdated"));
        }
      }
    } catch (error) {
      console.error("Error revealing contact:", error);
      toast.error("Failed to reveal contact details");
    } finally {
      setIsRevealing(false);
    }
  };

  const handleWishlist = async () => {
    if (!userData) {
      toast.info(t("common:loginRequiredWishlist") || "Please Login to add to Wishlist");
      return;
    }

    const action = isFavorite ? "remove" : "add";
    try {
      const response = await axios.post(`${url}/api/wishlist`, {
        userId: userData._id,
        plotId: crop._id,
        action,
      });

      if (response.status === 200) {
        setIsFavorite(!isFavorite);
        toast.success(
          action === "add" 
            ? (t("common:addedToWishlist") || "Added to Wishlist") 
            : (t("common:removedFromWishlist") || "Removed from Wishlist"),
        );
      }
    } catch (error) {
      console.error("Error updating wishlist:", error);
      toast.error(t("common:failedUpdateWishlist") || "Failed to update wishlist");
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress sx={{ color: colors.primary }} />
      </Box>
    );
  }

  if (!crop) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "60vh",
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontFamily: fonts.poppins, fontWeight: 700, mb: 2 }}
        >
          {t("common:cropNotFound") || "Crop Not Found"}
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/home")}
          sx={{ backgroundColor: colors.primary }}
        >
          {t("common:cropDetails.backToListings")}
        </Button>
      </Box>
    );
  }

  const photos = Array.isArray(crop.photos) ? crop.photos : [];

  // Member since year
  const memberSinceYear = seller?.createdAt
    ? new Date(seller.createdAt).getFullYear()
    : new Date().getFullYear() - 1;
  const sellerDisplayName = seller
    ? seller.firstName
      ? `${seller.firstName} ${seller.lastName || ""}`.trim()
      : seller.name || seller.username
    : "Farmer";
  const sellerAvatar = seller?.profilePicture
    ? seller.profilePicture.startsWith("http")
      ? seller.profilePicture
      : `${url}/${seller.profilePicture.replace(/\\/g, "/")}`
    : seller?.photoURL || null;

  const handleDelete = async () => {
    if (!window.confirm(t("common:deleteConfirm") || "Are you sure you want to delete this plot?")) return;
    try {
      setIsDeleting(true);
      await axios.delete(`${url}/api/plots/${id}`);
      toast.success(t("common:deleteSuccess") || "Plot deleted successfully");
      navigate("/home");
    } catch (err) {
      console.error("Error deleting plot:", err);
      toast.error(t("common:deleteFailed") || "Failed to delete plot");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        backgroundColor: "#FCFCFC",
        maxWidth: "1200px",
        margin: "0 auto",
        px: 0,
        py: { xs: 3, md: 4 },
      }}
    >
      {/* Back Button Container */}
      <Box sx={{ mb: 3 }}>
        <Button
          onClick={() => navigate(-1)}
          startIcon={<IconChevronLeft size={20} />}
          sx={{
            fontFamily: fonts.poppins,
            fontWeight: 700,
            fontSize: "15px",
            color: "#637381",
            textTransform: "none",
            borderRadius: "12px",
            px: 2,
            py: 1,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "#F4F6F8",
              color: "#1C252E",
              transform: "translateX(-4px)",
            },
          }}
        >
          {t("common:cropDetails.backToListings")}
        </Button>
      </Box>

      <Grid container spacing={5}>
        {/* --- LEFT SECTION: IMAGES & SELLER --- */}
        <Grid item xs={12} md={6}>
          {/* Main Image */}
          <Box
            sx={{
              position: "relative",
              width: isMobile ? "100%" : "453px",
              height: isMobile ? "300px" : "385px",
              backgroundColor: "#F4F6F8",
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0,0,0,0.06)",
              opacity: 1,
              mb: 2,
              mx: isMobile ? "auto" : "unset",
            }}
          >
            <Box
              component="img"
              src={
                photos.length > 0
                  ? `${url}/${photos[selectedImage]}?t=${new Date().getTime()}`
                  : "https://via.placeholder.com/453x385?text=No+Image"
              }
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>

          {/* Thumbnails dengan scroll */}
          {photos.length > 1 && (
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                overflowX: "auto",
                mb: 4,
                pb: 2,
                px: 0.5,
                width: isMobile ? "100%" : "453px",
                "&::-webkit-scrollbar": {
                  height: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#D1D5DB",
                  borderRadius: "10px",
                  "&:hover": { backgroundColor: "#9CA3AF" },
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#F3F4F6",
                  borderRadius: "10px",
                },
              }}
            >
              {photos.map((photo, index) => (
                <Box
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  sx={{
                    minWidth: "82px",
                    width: "82px",
                    height: "82px",
                    borderRadius: "12px",
                    flexShrink: 0,
                    overflow: "hidden",
                    cursor: "pointer",
                    border:
                      selectedImage === index
                        ? `3px solid ${colors.primary}`
                        : "2px solid #F3F4F6",
                    boxShadow:
                      selectedImage === index
                        ? `0 4px 12px ${colors.primary_rgba_24}`
                        : "0 2px 8px rgba(0,0,0,0.04)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      borderColor:
                        selectedImage === index ? colors.primary : "#D1D5DB",
                      boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={`${url}/${photo}?t=${new Date().getTime()}`}
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Box>
              ))}
            </Box>
          )}

          {/* Seller Card */}
          <Box
            sx={{
              backgroundColor: "#FFFFFF",
              borderRadius: "24px",
              p: 3,
              border: "1px solid #E0E5EB",
              boxShadow: "0 4px 12px rgba(0,0,0,0.02)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <Avatar
                src={sellerAvatar}
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "18px",
                   bgcolor: colors.primary_rgba_12,
                  color: colors.primary,
                  fontWeight: 700,
                  fontSize: "24px",
                }}
              >
                {sellerDisplayName.charAt(0)}
              </Avatar>
              <Box>
                <Typography
                  sx={{
                    fontFamily: fonts.poppins,
                    fontWeight: 700,
                    fontSize: "19px",
                    color: "#1C252E",
                  }}
                >
                  {sellerDisplayName}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <IconMapPin size={16} color={colors.primary} />
                  <Typography
                    sx={{
                      fontFamily: fonts.poppins,
                      fontSize: "14px",
                      color: "#637381",
                      fontWeight: 500
                    }}
                  >
                    {seller?.village ? `${seller.village}, ` : ""}
                    {seller?.tahsil || seller?.district || "Location"}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                color: "#637381",
                mb: 3,
                px: 0.5,
              }}
            >
              <IconUsers size={20} color="#919EAB" />
              <Typography
                sx={{
                  fontFamily: fonts.poppins,
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                {t("common:cropDetails.memberSince")}{" "}
                {new Date().getFullYear() - memberSinceYear > 0
                  ? `${new Date().getFullYear() - memberSinceYear} ${t("common:cropDetails.years")}`
                  : t("common:cropDetails.thisYear")}
              </Typography>
            </Box>

            {/* --- ROLE BASED ACTION AREA --- */}
            <Box
              sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: "16px",
                p: 2,
                textAlign: "center",
                border: "1px solid #F4F6F8",
              }}
            >
              {isOwner ? (
                <>
                  <Typography
                    sx={{
                      fontFamily: fonts.poppins,
                      fontSize: "14px",
                      fontWeight: 600,
                      color: colors.primary,
                      mb: 1,
                    }}
                  >
                    {t("common:cropDetails.thisIsYourListing")}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() =>
                        navigate("/add-crop-step-1", {
                          state: {
                            isEdit: true,
                            cropId: crop._id,
                            cropData: crop,
                          },
                        })
                      }
                      sx={{
                        backgroundColor: colors.primary,
                        color: '#FFFFFF',
                        borderRadius: "12px",
                        textTransform: "none",
                        fontWeight: 700,
                        height: '44px',
                        "&:hover": { backgroundColor: colors.primary_dark },
                      }}
                    >
                      {t("common:cropDetails.editDetails")}
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => setIsSponsorPopupOpen(true)}
                      startIcon={<IconBolt size={18} />}
                      sx={{
                        backgroundColor: '#FFAB00',
                        color: '#FFFFFF',
                        borderRadius: "12px",
                        textTransform: "none",
                        fontWeight: 700,
                        height: '44px',
                        "&:hover": { backgroundColor: '#E69900' },
                      }}
                    >
                      {t("common:cropDetails.sponsorPlot")}
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handleDelete}
                      disabled={isDeleting}
                      startIcon={<IconTrash size={18} />}
                      sx={{
                        borderColor: '#FF4842',
                        color: '#FF4842',
                        borderRadius: "12px",
                        textTransform: "none",
                        fontWeight: 700,
                        height: '44px',
                        "&:hover": {
                          borderColor: '#B71D18',
                          backgroundColor: 'rgba(255, 72, 66, 0.08)',
                        },
                      }}
                    >
                      {isDeleting ? t("common:cropDetails.deleting") : t("common:cropDetails.deletePlot")}
                    </Button>
                  </Box>
                </>
              ) : userData?.role === "Seller" || userData?.role === "Farmer" ? (
                <>
                  <Typography
                    sx={{
                      fontFamily: fonts.poppins,
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#637381",
                      mb: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    <IconInfoCircle size={18} /> {t("common:cropDetails.needContactDetails")}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: fonts.poppins,
                      fontSize: "12px",
                      color: "#919EAB",
                      mb: 2,
                    }}
                  >
                    {t("common:cropDetails.buyerAccountRequiredDesc")}
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => setShowLogoutPrompt(true)}
                    sx={{
                      backgroundColor: colors.primary,
                      borderRadius: "12px",
                      textTransform: "none",
                      fontWeight: 700,
                      "&:hover": { backgroundColor: colors.primary_dark },
                    }}
                  >
                    {t("common:cropDetails.createBuyerAccount")}
                  </Button>
                </>
              ) : (hasPurchased || isRevealed) ? (
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<IconPhone size={18} />}
                    onClick={() => {
                      window.location.href = `tel:${crop.contactNumber}`;
                    }}
                    sx={{
                      height: "48px",
                      backgroundColor: colors.primary,
                      borderRadius: "12px",
                      textTransform: "none",
                      fontWeight: 700,
                      fontFamily: fonts.poppins,
                      "&:hover": { backgroundColor: colors.primary_dark },
                    }}
                  >
                    {t("callBtn") || "Call"}
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<IconBrandWhatsapp size={18} />}
                    onClick={() => {
                      window.open(
                        `https://wa.me/${crop.contactNumber}`,
                        "_blank"
                      );
                    }}
                    sx={{
                      height: "48px",
                      backgroundColor: "#25D366",
                      borderRadius: "12px",
                      textTransform: "none",
                      fontWeight: 700,
                      fontFamily: fonts.poppins,
                      "&:hover": { backgroundColor: "#128C7E" },
                    }}
                  >
                    {t("common:whatsappBtn") || "WhatsApp"}
                  </Button>
                </Box>
              ) : (
                <>
                  <Typography
                    sx={{
                      fontFamily: fonts.poppins,
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#637381",
                      mb: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    <IconEye size={18} stroke={2.5} /> {t("common:cropDetails.contactDetailsHidden")}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: fonts.poppins,
                      fontSize: "12px",
                      color: "#919EAB",
                    }}
                  >
                    {t("common:cropDetails.revealContactDesc")}
                  </Typography>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleGetContact}
                    disabled={isRevealing}
                    sx={{
                      mt: 2,
                      backgroundColor: colors.primary,
                      borderRadius: "12px",
                      textTransform: "none",
                      fontWeight: 700,
                      fontFamily: fonts.poppins,
                      height: "48px",
                      "&:hover": { backgroundColor: colors.primary_dark },
                    }}
                  >
                    {isRevealing ? (
                      <CircularProgress size={24} sx={{ color: "white" }} />
                    ) : (
                      <>
                        <IconEye size={18} style={{ marginRight: "8px" }} />{" "}
                        {t("common:cropDetails.showContactDetails")}
                      </>
                    )}
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Grid>

        {/* --- RIGHT SECTION: DETAILS --- */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 2,
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontFamily: fonts.poppins,
                fontWeight: 800,
                color: "#1C252E",
              }}
            >
              {t(`crops.${crop.subCategory || crop.name || ''}`, { defaultValue: crop.subCategory || crop.name })}
            </Typography>
            <IconButton
              onClick={handleWishlist}
              sx={{
                 color: isFavorite ? colors.primary : "#919EAB",
                transition: "all 0.2s ease",
                "&:hover": { transform: "scale(1.1)", color: colors.primary },
              }}
            >
              <IconHeart
                size={28}
                 fill={isFavorite ? colors.primary : "none"}
                stroke={2.5}
              />
            </IconButton>
          </Box>

          {/* Tags */}
          <Box sx={{ display: "flex", gap: 1.5, mb: 4, flexWrap: "wrap" }}>
            {Object.values(crop.deliveryOptions || {}).some(
              (v) => v === true,
            ) && (
              <Chip
                label={t("common:cropDetails.deliveryAvailable")}
                sx={{
                  borderRadius: "8px",
                  fontWeight: 600,
                  p: 0.5,
                  bgcolor: "#F4F6F8",
                  color: "#637381",
                }}
              />
            )}
            <Chip
              label={`${t("common:gradeBadge")} ${crop.grade || "A"}`}
              sx={{
                borderRadius: "8px",
                fontWeight: 600,
                p: 0.5,
                bgcolor: "#F4F6F8",
                color: "#637381",
              }}
            />
            {crop.isOrganic && (
              <Chip
                label={t("common:cropDetails.organic")}
                sx={{
                  borderRadius: "8px",
                  fontWeight: 600,
                  p: 0.5,
                  bgcolor: "#F4F6F8",
                  color: "#637381",
                }}
              />
            )}
          </Box>

          {/* Price Section */}
          <Box
            sx={{
              backgroundColor: "#FFFFFF",
              borderRadius: "24px",
              p: 3,
              mb: 4,
              border: "1px solid #F4F6F8",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mb: 3 }}
            >
              <Typography
                sx={{
                  fontSize: "36px",
                   color: colors.primary,
                  fontWeight: 800,
                  fontFamily: fonts.poppins,
                }}
              >
                ₹{crop.amount || "0"}
              </Typography>
              <Typography
                sx={{ fontSize: "18px", color: "#637381", fontWeight: 600 }}
              >
                / {crop.priceUnit || "Kg"}
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: "#919EAB",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                    mb: 0.5,
                  }}
                >
                  {t("common:cropDetails.variety")}
                </Typography>
                <Typography
                  sx={{ fontSize: "16px", fontWeight: 700, color: "#1C252E" }}
                >
                  {crop.variety || t("common:cropDetails.standard")}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: "#919EAB",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                    mb: 0.5,
                  }}
                >
                  {t("common:cropDetails.availableLabel")}
                </Typography>
                <Typography
                  sx={{ fontSize: "16px", fontWeight: 700, color: "#1C252E" }}
                >
                  {crop.area} {crop.areaUnit}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Description */}
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 700,
                color: "#1C252E",
                mb: 1,
                fontFamily: fonts.poppins,
              }}
            >
              {t("common:cropDetails.description")}
            </Typography>
            <Typography
              sx={{ color: "#637381", lineHeight: 1.6, fontSize: "15px" }}
            >
              {crop.additionalNotes ||
                crop.description ||
                t("common:cropDetails.noDetailsProvided")}
            </Typography>
          </Box>

          <Divider sx={{ mb: 4, borderStyle: "dashed" }} />

          {/* Location & Delivery */}
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: 700,
                color: "#1C252E",
                mb: 2,
                fontFamily: fonts.poppins,
              }}
            >
              {t("common:cropDetails.locationAndDelivery")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                 color: colors.primary,
                fontWeight: 700,
                mb: 2.5,
              }}
            >
              <IconMapPin size={22} stroke={2.5} />
              <Typography sx={{ fontSize: "15px", fontWeight: 600 }}>
                {crop.village ? `${crop.village}, ` : ""}
                {crop.taluka || crop.district || (t("common:locationFallback") || "Location")}, {crop.state}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              {crop.deliveryOptions?.selfPickup && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                     bgcolor: colors.primary_rgba_8,
                    color: colors.primary,
                    px: 1.5,
                    py: 0.8,
                    borderRadius: "8px",
                  }}
                >
                  <IconCheck size={16} stroke={3} />
                  <Typography sx={{ fontSize: "13px", fontWeight: 700 }}>
                    {t("common:cropListing.selfPickup") || "Self Pickup"}
                  </Typography>
                </Box>
              )}
              {crop.deliveryOptions?.transportAvailable && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                     bgcolor: colors.primary_rgba_8,
                    color: colors.primary,
                    px: 1.5,
                    py: 0.8,
                    borderRadius: "8px",
                  }}
                >
                  <IconCheck size={16} stroke={3} />
                  <Typography sx={{ fontSize: "13px", fontWeight: 700 }}>
                    {t("common:cropListing.transportAvailable") || "Transport Available"}
                  </Typography>
                </Box>
              )}
              {crop.deliveryOptions?.localDelivery && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.8,
                     bgcolor: colors.primary_rgba_8,
                    color: colors.primary,
                    px: 1.5,
                    py: 0.8,
                    borderRadius: "8px",
                  }}
                >
                  <IconCheck size={16} stroke={3} />
                  <Typography sx={{ fontSize: "13px", fontWeight: 700 }}>
                    {t("common:cropListing.localDelivery") || "Local Delivery"}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Logistics Advisory */}
          <Box
            sx={{
              backgroundColor: "#FFFFFF",
              borderRadius: "16px",
              p: 2,
              display: "flex",
              gap: 1.5,
              alignItems: "flex-start",
              border: "1px solid #E0E5EB",
            }}
          >
            <IconInfoCircle size={22} color="#919EAB" />
            <Typography
              sx={{ fontSize: "13px", color: "#637381", lineHeight: 1.5 }}
            >
              {t("common:cropDetails.logisticsAdvisory")}
            </Typography>
          </Box>
          {/* History Sections for Owner */}
          {isOwner && (
            <Box sx={{ mt: 6 }}>
              <Divider sx={{ mb: 4, borderStyle: "dashed" }} />
              
              {/* Sponsorship Management */}
              <Box sx={{ mb: 6 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <IconBolt size={24} color="#FFAB00" />
                    <Typography sx={{ fontSize: "20px", fontWeight: 800, fontFamily: fonts.poppins }}>
                      {t("common:cropDetails.sponsorshipHistory")}
                    </Typography>
                  </Box>
                  <Button 
                    variant="text" 
                    onClick={() => setIsSponsorPopupOpen(true)}
                    endIcon={<IconArrowRight size={18} />}
                    sx={{ fontWeight: 700, color: colors.primary }}
                  >
                    {t("common:cropDetails.scheduleNew")}
                  </Button>
                </Box>

                {sponsorshipHistory.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#F4F6F8', borderRadius: '16px' }}>
                    <Typography sx={{ color: '#637381', fontSize: '14px' }}>
                      {t("common:cropDetails.noSponsorshipHistory")}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {sponsorshipHistory.map((history) => (
                      <Box 
                        key={history._id}
                        sx={{ 
                          p: 2, 
                          borderRadius: '16px', 
                          border: '1px solid #E0E5EB', 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          bgcolor: history.status === 'active' ? 'rgba(255, 171, 0, 0.08)' : '#FFFFFF'
                        }}
                      >
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '15px' }}>
                              {history.hours} {t("common:cropDetails.hoursBoost")}
                            </Typography>
                            <Chip 
                              label={history.status.toUpperCase()} 
                              size="small" 
                              sx={{ 
                                height: '20px', 
                                fontSize: '10px', 
                                fontWeight: 800,
                                bgcolor: history.status === 'active' ? '#FFAB00' : history.status === 'completed' ? '#00A76F' : '#919EAB',
                                color: '#FFF'
                              }} 
                            />
                          </Box>
                          <Typography sx={{ fontSize: '13px', color: '#637381' }}>
                            {dayjs(history.startTime).format("MMM D, YYYY • h:mm A")} to {dayjs(history.expiresAt).format("h:mm A")}
                          </Typography>
                        </Box>
                        {history.status === 'active' && (
                          <IconBolt size={24} color="#FFAB00" style={{ opacity: 0.6 }} />
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Buyer/Contact History */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
                  <IconUsers size={24} color={colors.primary} />
                  <Typography sx={{ fontSize: "20px", fontWeight: 800, fontFamily: fonts.poppins }}>
                    {t("common:cropDetails.contactedBuyers")}
                  </Typography>
                </Box>

                {!crop.purchasedBy || crop.purchasedBy.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#F4F6F8', borderRadius: '16px' }}>
                    <Typography sx={{ color: '#637381', fontSize: '14px' }}>
                      {t("common:cropDetails.noBuyersContacted")}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {crop.purchasedBy.map((p, idx) => (
                      <Box 
                        key={idx}
                        sx={{ 
                          p: 2, 
                          borderRadius: '16px', 
                          border: '1px solid #E0E5EB', 
                          display: 'flex', 
                          alignItems: 'center',
                          gap: 2,
                          bgcolor: '#FFFFFF'
                        }}
                      >
                        <Avatar 
                          src={p.userId?.profilePicture ? `${url}/${p.userId.profilePicture}` : ""} 
                          sx={{ width: 44, height: 44, bgcolor: colors.primary_rgba_8, color: colors.primary, fontWeight: 700 }}
                        >
                          {p.userId?.firstName?.[0] || "B"}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '15px' }}>
                            {p.userId?.firstName} {p.userId?.lastName}
                          </Typography>
                          <Typography sx={{ fontSize: '13px', color: '#637381' }}>
                            {t("common:cropDetails.contactedOn")} {dayjs(p.purchaseDate).format("MMM D, YYYY")}
                          </Typography>
                        </Box>
                        <IconButton 
                          href={`tel:${p.userId?.phoneNumber}`}
                          sx={{ color: colors.primary, bgcolor: colors.primary_rgba_8 }}
                        >
                          <IconPhone size={20} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>
      
      {/* Sponsorship Popup */}
      <SponsoredHoursPopup
        open={isSponsorPopupOpen}
        onClose={() => setIsSponsorPopupOpen(false)}
        user={userData}
        plot={crop}
        onUpdateUser={(updatedUser) => {
          localStorage.setItem("userData", JSON.stringify(updatedUser));
          window.location.reload(); // Quick way to sync state
        }}
      />

      {/* --- LOGOUT CONFIRMATION DIALOG --- */}
      <Dialog
        open={showLogoutPrompt}
        onClose={() => setShowLogoutPrompt(false)}
        PaperProps={{
          sx: { borderRadius: "20px", p: 1, maxWidth: "400px" },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: fonts.poppins,
            fontWeight: 800,
            fontSize: "22px",
            textAlign: "center",
            pb: 1,
          }}
        >
          {t("common:cropDetails.switchAccountRole")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              fontFamily: fonts.poppins,
              fontSize: "14px",
              color: "#637381",
              textAlign: "center",
              px: 2,
            }}
          >
            {t("common:cropDetails.switchRoleDesc")}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1, flexDirection: "column" }}>
          <Button
            fullWidth
            onClick={() => {
              localStorage.clear();
              // Redirect to home and trigger login popup
              window.location.href = "/home?openLogin=true";
            }}
            sx={{
              fontFamily: fonts.poppins,
              fontWeight: 700,
              backgroundColor: "#FF4842",
              color: "#FFFFFF",
              textTransform: "none",
              borderRadius: "12px",
              py: 1.5,
              "&:hover": { backgroundColor: "#B71D18" },
            }}
            variant="contained"
          >
            {t("common:cropDetails.yesLogout")}
          </Button>
          <Button
            fullWidth
            onClick={() => setShowLogoutPrompt(false)}
            sx={{
              fontFamily: fonts.poppins,
              fontWeight: 700,
              color: "#637381",
              textTransform: "none",
              borderRadius: "12px",
              py: 1.2,
            }}
          >
            {t("common:cancel")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CropDetails;
